import { useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, KeyRound } from "lucide-react";
import Logo from "@/components/Logo";
import { API, setAuth } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * Public page. Handles both:
 *   - /accept-invite/:token  (new client sets first password)
 *   - /reset-password/:token (existing user resets)
 */
export default function AcceptInvite({ mode = "invite" }) {
  const { token } = useParams();
  const nav = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth() || {};

  const isInvite = mode === "invite";
  const title = isInvite ? "Welcome to HBT" : "Reset your password";
  const eyebrow = isInvite ? "Account setup" : "Password reset";
  const subtitle = isInvite
    ? "Choose a password to activate your client portal."
    : "Choose a new password for your HBT account.";
  const cta = isInvite ? "Create account" : "Update password";
  const endpoint = isInvite ? "/auth/accept-invite" : "/auth/reset-password";

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      toast.error("Passwords do not match.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}${endpoint}`, { token, password });
      if (isInvite && data?.token) {
        setAuth(data.token, data.refresh_token, data.user);
        // Rehydrate context user by triggering login state via a full navigation
        window.location.href = "/app";
        return;
      }
      toast.success("Password updated. You can sign in now.");
      nav("/login");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "This link is invalid or expired.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2" style={{ background: "var(--bone)" }}>
      <div className="flex flex-col justify-center px-8 md:px-16 lg:px-24 py-14">
        <Link to="/" className="self-start mb-14 hover:opacity-80 transition-opacity" data-testid="invite-back-home">
          <Logo />
        </Link>

        <div className="max-w-md w-full">
          <div className="hbt-label mb-4 flex items-center gap-2"><KeyRound className="w-3 h-3" /> {eyebrow}</div>
          <h1 className="font-display font-semibold text-4xl md:text-5xl tracking-tight leading-tight">{title}</h1>
          <p className="text-neutral-500 mt-3 text-sm">{subtitle}</p>

          <form onSubmit={submit} className="mt-10 space-y-5" data-testid="invite-form">
            <div>
              <label className="hbt-label block mb-2">New password</label>
              <input
                type="password" required minLength={8}
                className="hbt-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="At least 8 characters"
                data-testid="invite-password-input"
              />
            </div>
            <div>
              <label className="hbt-label block mb-2">Confirm password</label>
              <input
                type="password" required minLength={8}
                className="hbt-input"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                data-testid="invite-confirm-input"
              />
            </div>
            <button type="submit" disabled={loading || !token} className="btn-primary w-full flex items-center justify-center gap-2" data-testid="invite-submit-btn">
              {loading ? "Saving…" : (<>{cta} <ArrowRight className="w-4 h-4" /></>)}
            </button>
          </form>

          <div className="mt-8 text-xs text-neutral-500">
            Already have a password? <Link to="/login" className="text-[var(--forest)] font-semibold hover:underline">Sign in</Link>
          </div>
        </div>
      </div>

      <div className="hidden lg:block relative overflow-hidden" style={{ background: "var(--forest)" }}>
        <img src="/logo/login-bg.webp" className="absolute inset-0 w-full h-full object-cover opacity-50" alt="" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(26,54,45,0.55), rgba(26,54,45,0.85))" }} />
        <div className="relative z-10 h-full flex flex-col justify-between p-14 text-white">
          <div className="hbt-label" style={{ color: "rgba(255,255,255,0.7)" }}>HBT Waste Management</div>
          <div>
            <div className="font-display font-semibold text-4xl xl:text-5xl tracking-tight leading-tight max-w-lg">
              A single portal for waste, licences and ESG — measurable, verifiable, yours.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
